import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { UploadMultiImages } from "@/components/ui/upload-multi-images";
import { UploadMultiFiles } from "@/components/ui/upload-multi-files";
import { FoodDrinksActivity } from "../models/activity.model";
import { Link, ArrowRight } from "lucide-react";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { useI18n } from "@/hooks/useI18n";
import ActivityModalShell from "./ActivityModalShell";

type FoodDrinksTab = 'details' | 'media' | 'documents';

interface FoodDrinksModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (activity: Partial<FoodDrinksActivity>) => void;
  icon?: React.ReactNode;
  initialData?: Partial<FoodDrinksActivity>;
  itineraryId: string;
  itineraryStartDate?: string | null;
  itineraryEndDate?: string | null;
  defaultDate?: string;
}

const FoodDrinksModal = ({ isOpen, onClose, onSave, icon, initialData, itineraryId, itineraryStartDate, itineraryEndDate, defaultDate }: FoodDrinksModalProps) => {
  const { t } = useI18n();

  // Generate temporary UUID for new activities
  const [activityId] = useState<string>(() =>
    initialData?.id || crypto.randomUUID()
  );

  const [formData, setFormData] = useState<Partial<FoodDrinksActivity>>({
    id: activityId,
    title: '',
    description: '',
    link: '',
    media: [],
    documentation: []
  });

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData({ ...initialData, id: activityId });
    }
  }, [initialData, activityId]);

  const derivedMinDate: string | null =
    itineraryStartDate
    || (initialData as any)?.itineraryStartDate
    || (initialData as any)?.itinerary_start_date
    || (initialData as any)?.min_date
    || (initialData as any)?.minDate
    || null;

  const derivedMaxDate: string | null =
    itineraryEndDate
    || (initialData as any)?.itineraryEndDate
    || (initialData as any)?.itinerary_end_date
    || (initialData as any)?.max_date
    || (initialData as any)?.maxDate
    || null;

  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [showValidationBanner, setShowValidationBanner] = useState(false);
  const [activeTab, setActiveTab] = useState<FoodDrinksTab>('details');

  useEffect(() => {
    if (!isOpen) {
      setShowValidationBanner(false);
      setErrors({});
      setActiveTab('details');
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !initialData && defaultDate) {
      setFormData(prev => ({ ...prev, start_date: defaultDate, end_date: defaultDate }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const validateForm = (): Record<string, boolean> => {
    const requiredFields = ['title', 'description'];
    const newErrors: Record<string, boolean> = {};

    requiredFields.forEach(field => {
      const value = formData[field as keyof FoodDrinksActivity];
      newErrors[field] = !value || (typeof value === 'string' && value.trim() === '');
    });

    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (!Object.values(newErrors).some(error => error)) {
      onSave({
        ...formData,
        type: 'food-drinks' as const
      });
      onClose();
      return;
    }
    setShowValidationBanner(true);
    setActiveTab('details');
  };

  const hasDetailsError = ['title', 'description'].some(field => errors[field]);

  return (
    <ActivityModalShell
      isOpen={isOpen}
      onClose={onClose}
      icon={icon}
      title={t('builder.activities.foodDrinks')}
      formId="food-drinks-form"
      onSubmit={handleSubmit}
      showValidationBanner={showValidationBanner}
      activeTab={activeTab}
      onActiveTabChange={(value) => setActiveTab(value as FoodDrinksTab)}
      tabs={[
        {
          key: 'details',
          label: t('modals.tabDetails'),
          hasError: hasDetailsError,
          content: (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>{t('modals.title')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <Input
                    id="title"
                    value={formData.title || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder={t('modals.restaurantName')}
                    hasError={errors.title}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>{t('modals.description')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <RichTextEditor
                    value={formData.description || ''}
                    onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                    placeholder={t('modals.describeRestaurant')}
                    hasError={errors.description}
                  />
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle>{t('modals.date')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <DateRangePicker
                      startDate={formData.start_date || ''}
                      endDate={formData.end_date || ''}
                      onStartDateChange={(date) => setFormData(prev => ({ ...prev, start_date: date }))}
                      onEndDateChange={(date) => setFormData(prev => ({ ...prev, end_date: date }))}
                      startDatePlaceholder={t('common.from')}
                      endDatePlaceholder={t('common.to')}
                      hasError={false}
                      minDate={derivedMinDate || undefined}
                      maxDate={derivedMaxDate || undefined}
                    />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>{t('modals.time')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-row justify-evenly items-center">
                      <div className="flex flex-row items-center">
                        <Input
                          className="w-30"
                          type="time"
                          value={formData.start_time || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                          placeholder={t('common.from')}
                        />
                      </div>
                      <span>
                        <ArrowRight className="mx-2 h-4 w-4 text-gray-500" />
                      </span>
                      <div className="flex flex-row items-center">
                        <Input
                          className="w-30"
                          type="time"
                          value={formData.end_time || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                          placeholder={t('common.to')}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>{t('modals.link')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <label htmlFor="link" className="text-sm">{t('modals.addSupportingLink')}</label>
                  <div className="relative mt-3">
                    <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-900" />
                    <Input
                      id="link"
                      type="url"
                      value={formData.link || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value }))}
                      placeholder={t('modals.link')}
                      className="pl-10"
                      hasError={errors.link}
                    />
                  </div>
                </CardContent>
              </Card>
            </>
          ),
        },
        {
          key: 'media',
          label: t('modals.tabMedia'),
          content: (
            <Card>
              <CardHeader>
                <CardTitle>{t('modals.media')}</CardTitle>
              </CardHeader>
              <CardContent>
                <UploadMultiImages
                  images={formData.media || []}
                  onImagesChange={(images) => setFormData(prev => ({ ...prev, media: images }))}
                  label={t('modals.upload4Photos')}
                  maxImages={10}
                  itineraryId={itineraryId}
                  activityId={activityId}
                />
              </CardContent>
            </Card>
          ),
        },
        {
          key: 'documents',
          label: t('modals.tabDocuments'),
          content: (
            <Card>
              <CardHeader>
                <CardTitle>{t('modals.documentation')}</CardTitle>
              </CardHeader>
              <CardContent>
                <UploadMultiFiles
                  files={formData.documentation || []}
                  onFilesChange={(files) => setFormData(prev => ({ ...prev, documentation: files }))}
                  label={t('modals.uploadBookingDocumentation')}
                  maxFiles={5}
                  itineraryId={itineraryId}
                  activityId={activityId}
                />
              </CardContent>
            </Card>
          ),
        },
      ]}
    />
  );
};

export default FoodDrinksModal;
